import styled, { css } from 'styled-components';
import { ifProp, palette, theme } from 'styled-tools';

import { units } from '../../utils/spacing';
import { fontSize, FontSize, FontSizes, FontWeights, lineHeight } from '../../utils/typography';
import Text from '../Text';
import { TextElements } from '../Text/types';
import { CheckboxAlign, CheckboxAlignment, CheckboxVerticalAlign, CheckboxVerticalAlignment } from './types';

export const CheckboxBox = styled.div<{
	$isFocusVisible: boolean;
	$isSelected: boolean;
	$isStrong?: boolean;
	$isValid?: boolean;
}>`
	background-color: ${p => (p.$isSelected ? palette('checkboxSelectedBg') : palette('checkboxDeselectedBg'))};
	border: ${p => (p.$isStrong ? '2px' : '1px')} solid;
	border-color: ${p =>
		p.$isValid
			? p.$isSelected
				? palette('checkboxSelectedBorder')
				: p.$isStrong
				? palette('strongCheckboxDeselectedBorder')
				: palette('checkboxDeselectedBorder')
			: palette('errorRed')};
	border-radius: 2px;
	width: 1rem;
	height: 1rem;
	margin-inline-end: ${p => (p.$isStrong ? '1rem' : '0.5rem')};
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	flex-shrink: 0;
	margin-top: ${p => (p.$isStrong ? units(0.5) : '0')};

	svg {
		height: 20px;
		width: 20px;
		path {
			fill: ${palette('checkboxCheckFillColor')};
			stroke: ${palette('checkboxCheckStrokeColor')};
		}
	}

	&:after {
		content: '';
		display: block;
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		border: 2px solid ${palette('focusRingDark')};
		border-radius: 4px;
		transition: margin ${theme('animation.transition')}, opacity ${theme('animation.transition')};
		margin: ${p => (p.$isFocusVisible ? '-6px;' : '0;')};
		opacity: ${p => (p.$isFocusVisible ? '1' : '0')};
	}

	&:before {
		background: rgba(0, 0, 0, 0.15);
		content: '';
		left: -1px;
		height: calc(100% + 2px);
		border-radius: 4px;
		opacity: ${p => (p.$isFocusVisible && !p.$isSelected ? '1' : '0')};
		position: absolute;
		top: -1px;
		width: calc(100% + 2px);
	}
`;

export const LabelWrapperComponent = styled(Text)<{
	$alignment: CheckboxAlignment;
	$isSelected: boolean;
	$verticalAlignment: CheckboxVerticalAlignment;
	$textSize?: FontSize;
	$disabled?: boolean;
}>`
	display: flex;
	align-items: ${p => {
		switch (p.$verticalAlignment) {
			case CheckboxVerticalAlign.Center: {
				return 'center';
			}
			case CheckboxVerticalAlign.Top: {
				return 'flex-start';
			}
		}
	}};
	justify-content: ${props => {
		switch (props.$alignment) {
			case CheckboxAlign.Left: {
				return 'left';
			}
			case CheckboxAlign.Right: {
				return 'right';
			}
			case CheckboxAlign.Center:
				return 'center';
		}
	}};
	${p => (p.$disabled ? '' : 'cursor: pointer')};
	position: relative;
	${p => fontSize(p.$textSize ?? p.theme.checkboxes.textSize)}
	${p => lineHeight(p.$textSize ?? p.theme.checkboxes.textSize)}
	${p => (p.$disabled ? 'opacity: 0.6' : '')};

	&:hover {
		${CheckboxBox}:before {
			opacity: ${p => (p.$isSelected || p.$disabled ? '0' : '1')};
		}
	}
`;

export const LabelWrapper = styled(LabelWrapperComponent).attrs({
	el: TextElements.Label,
})``;

export const Container = styled.div<{ $isHorizontalError?: boolean }>`
	display: flex;
	flex-direction: ${p => (p.$isHorizontalError ? 'row' : 'column')};
`;

export const ErrorMessage = styled(Text).attrs({
	el: 'p',
	size: FontSizes.sm,
	color: 'errorRed',
	weight: FontWeights.Medium,
	noMargin: true,
})`
	${ifProp(
		'isHorizontalError',
		css`
			margin-left: ${units(1)};
		`,
		css`
			margin-top: ${units(1)};
		`,
	)}
`;
